import React, { useState, useCallback, useEffect, useMemo } from 'react';
import isEqual from 'lodash/isEqual';
import moment from 'moment';

import {
    useActivateClosingMutation,
    useCreateClosingMutation,
    useGetCapitalCallsQuery,
    useGetClosingsQuery,
    useGetInvestmentFundsQuery,
    useSimulateClosingMutation,
    useTestClosingMutation,
    useUpdateClosingMutation,
} from 'api/main';
import { closingSchemas } from 'schemas';
import { closingPremium, billStatus as closingStatus } from 'utils/constants';
import { parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    ActionButton,
    ActionsMenu,
    ConfirmDialog,
    CustomDatePicker,
    CustomPaper,
    CustomSelect,
    CustomTextField,
    EmptyTableBody,
    ListContainer,
    Loader,
} from 'components';
import { MenuActivate, MenuSimulate, MenuTest } from 'components/ActionsMenu/actions';
import {
    Box,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';

const tomorrow = moment().add(1, 'd');
const getDefaultForm = () => ({
    fundId: '',
    premium: closingPremium.WITHOUT_PREMIUM,
    date: tomorrow,
    number: '',
});
const boxSx = theme => ({
    width: '40%',
    margin: '0 auto',
    [theme.breakpoints.down('md')]: { width: '100%' },
});
const parsePremium = premium => {
    if (premium === closingPremium.WITHOUT_PREMIUM) return texts.WITHOUT_PREMIUM;
    if (premium === closingPremium.WITH_PREMIUM) return texts.WITH_PREMIUM;
    return '-';
};
const defaultFormErrors = { number: '', date: '' };

const SimulateActionItem = React.forwardRef((props, ref) => <MenuSimulate ref={ref} {...props} />);
const ActivateActionItem = React.forwardRef((props, ref) => <MenuActivate ref={ref} {...props} />);
const TestActionItem = React.forwardRef((props, ref) => <MenuTest ref={ref} {...props} />);

const getMinDate = (closings, fundraisings, fundId) => {
    let lastDate = tomorrow.clone();

    if (fundId) {
        lastDate = closings.reduce((maxDate, c) => {
            if (
                c.investmentFund.id === fundId &&
                (c.status === closingStatus.COMPLETED || c.status === closingStatus.ACTIVATED)
            ) {
                return maxDate.isSameOrAfter(moment(c.issueDate).add(1, 'd'), 'd')
                    ? maxDate
                    : moment(c.issueDate).add(1, 'd');
            } else {
                return maxDate;
            }
        }, lastDate);
        lastDate = fundraisings.reduce((maxDate, f) => {
            if (
                f.investmentFund === '/api/investment_funds/' + fundId &&
                (f.status === closingStatus.COMPLETED || f.status === closingStatus.ACTIVATED)
            ) {
                return maxDate.isSameOrAfter(moment(f.issueDate).add(1, 'd'), 'd')
                    ? maxDate
                    : moment(f.issueDate).add(1, 'd');
            } else {
                return maxDate;
            }
        }, lastDate);
    }

    return lastDate;
};

const Closings = () => {
    const {
        data: fundList = [],
        isFetching: fundsLoader,
        isSuccess: fundsFetched,
    } = useGetInvestmentFundsQuery({
        visible: 1,
        firstInstalmentPercentage: 0,
        withCompletedFundraising: 1,
    });
    const { data: closings = [], isLoading: dataLoader } = useGetClosingsQuery({
        pagination: false,
    });
    const { data: fundraisings = [], isLoading: fundraisingLoader } = useGetCapitalCallsQuery({
        pagination: false,
    });

    const [createClosing, { isLoading: createLoading }] = useCreateClosingMutation();
    const [updateClosing, { isLoading: updateLoading }] = useUpdateClosingMutation();
    const [testClosing, { isLoading: testLoading }] = useTestClosingMutation();
    const [simulateClosing, { isLoading: simulationLoading }] = useSimulateClosingMutation();
    const [
        activateClosing,
        { isLoading: activateLoading, isSuccess: activateSuccess, reset: activateReset },
    ] = useActivateClosingMutation();

    const [form, setForm] = useState(getDefaultForm());
    const [formError, setFormError] = useState(defaultFormErrors);
    const [selectedFund, setSelectedFund] = useState('');
    const [activateDialogOpen, setActivateDialogOpen] = useState(false);

    const latestClosing = useMemo(
        () => closings.find(r => r.status === closingStatus.CREATED),
        [closings]
    );

    const activatedClosings = useMemo(
        () => closings.filter(r => r.status === closingStatus.ACTIVATED),
        [closings]
    );

    const completedClosings = useMemo(
        () => closings.filter(r => r.status === closingStatus.COMPLETED),
        [closings]
    );

    const filteredClosings = useMemo(
        () =>
            [...activatedClosings, ...completedClosings].filter(
                c => c.investmentFund.id === +selectedFund
            ),
        [selectedFund, activatedClosings, completedClosings]
    );
    const defaultFormValue = useMemo(() => {
        if (latestClosing) {
            return {
                fundId: latestClosing.investmentFund.id,
                premium: latestClosing.premium,
                date: moment(latestClosing.issueDate),
                number: latestClosing.comment,
            };
        } else return getDefaultForm();
    }, [latestClosing]);
    const changesMade = useMemo(() => !isEqual(defaultFormValue, form), [form, defaultFormValue]);

    const handleChange = useCallback(
        key => e => {
            if (key === 'fundId') {
                const minDate = getMinDate(closings, fundraisings, +e.target.value);
                setForm(prevState => ({
                    ...prevState,
                    [key]: e.target.value,
                    date: prevState.date.isBefore(minDate, 'd') ? minDate : prevState.date,
                }));
            } else {
                setForm(prevState => ({ ...prevState, [key]: e.target.value }));
            }
        },
        [closings, fundraisings]
    );

    const handleDateChange = useCallback(newDate => {
        setForm(prevState => ({
            ...prevState,
            date: newDate.startOf('day').add(newDate.utcOffset(), 'm'),
        }));
    }, []);

    const handleSimulate = useCallback(() => {
        simulateClosing({ investmentFundId: form.fundId });
    }, [simulateClosing, form.fundId]);

    const handleActivate = useCallback(() => {
        activateClosing({ investmentFundId: form.fundId });
    }, [activateClosing, form]);

    const handleTest = useCallback(() => {
        testClosing({ investmentFundId: form.fundId });
    }, [testClosing, form]);

    const minDate = useMemo(() => {
        return getMinDate(closings, fundraisings, form.fundId);
    }, [form, closings, fundraisings]);

    const handleSaveData = useCallback(() => {
        const errors = {};
        if (minDate.isAfter(form.date)) {
            errors.date = texts.DATE_LESS_THAN;
        }
        if (!form.number.trim()) {
            errors.number = texts.SAISIR_LE_CHAMP;
        }

        if (Object.keys(errors).length === 0) {
            setFormError(defaultFormErrors);
            if (latestClosing) {
                updateClosing(closingSchemas.updateClosing({ ...form, id: latestClosing.id }));
            } else {
                createClosing(closingSchemas.createClosing(form));
            }
        } else {
            setFormError(prevState => ({ ...prevState, ...errors }));
        }
    }, [minDate, form, latestClosing, updateClosing, createClosing]);

    const handleChangeSelectedFund = useCallback(e => setSelectedFund(e.target.value), []);

    useEffect(() => {
        if (fundsFetched) {
            setForm(prevState => ({
                ...prevState,
                fundId: latestClosing ? prevState.fundId : fundList[0].id,
            }));
            setSelectedFund(prevState => (!prevState ? fundList?.[0]?.id || '' : prevState));
        }
    }, [fundsFetched, fundList, latestClosing]);

    useEffect(() => {
        setForm(defaultFormValue);
    }, [defaultFormValue]);

    useEffect(() => {
        if (activateSuccess) {
            setActivateDialogOpen(false);
            activateReset();
        }
    }, [activateSuccess, activateReset]);

    return (
        <ListContainer>
            <Typography variant="h5" align="left" gutterBottom>
                {texts.CLOSINGS}
            </Typography>
            <CustomPaper>
                <Loader loading={fundsLoader || dataLoader || fundraisingLoader}>
                    <Box sx={boxSx}>
                        <CustomSelect
                            label={texts.FUND}
                            fullWidth
                            value={form.fundId}
                            onChange={handleChange('fundId')}
                        >
                            {fundList.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                        <CustomSelect
                            label={texts.PREMIUM_LABEL}
                            fullWidth
                            value={form.premium}
                            onChange={handleChange('premium')}
                        >
                            <MenuItem value={closingPremium.WITHOUT_PREMIUM}>
                                {texts.WITHOUT_PREMIUM}
                            </MenuItem>
                            <MenuItem value={closingPremium.WITH_PREMIUM}>
                                {texts.WITH_PREMIUM}
                            </MenuItem>
                        </CustomSelect>
                        <CustomDatePicker
                            fullWidth
                            label={texts.DATE}
                            value={form.date}
                            error={formError.date}
                            onChange={handleDateChange}
                            minDate={minDate}
                            showError={true}
                        />
                        <CustomTextField
                            formatNumber
                            fullWidth
                            label={texts.CLOSING_NBUMBER}
                            value={form.number}
                            error={formError.number}
                            onChange={handleChange('number')}
                        />
                    </Box>
                    <Box textAlign="center" paddingTop={4}>
                        <Loader loading={createLoading || updateLoading} marginTop={0}>
                            <ActionButton
                                disabled={createLoading || updateLoading || !changesMade}
                                onClick={handleSaveData}
                                variant="contained"
                            >
                                {texts.SAVE}
                            </ActionButton>
                        </Loader>
                    </Box>
                    <Box paddingTop={4} mb={5}>
                        {!(createLoading || updateLoading) && (
                            <ActionsMenu
                                autoClose
                                justify="center"
                                buttonStyle="text"
                                buttonText={texts.ENVOYER}
                                buttonProps={{ disabled: changesMade }}
                            >
                                <SimulateActionItem
                                    loading={simulationLoading}
                                    disabled={latestClosing?.status === closingStatus.ACTIVATED}
                                    onClick={handleSimulate}
                                />
                                <ActivateActionItem
                                    disabled={
                                        activateLoading || testLoading || !!activatedClosings.length
                                    }
                                    onClick={() => setActivateDialogOpen(true)}
                                />
                                <TestActionItem
                                    disabled={testLoading || activateLoading}
                                    onClick={handleTest}
                                />
                            </ActionsMenu>
                        )}
                    </Box>
                    <Box sx={boxSx}>
                        <CustomSelect
                            label={texts.CLOSING_HISTORY}
                            fullWidth
                            value={selectedFund}
                            onChange={handleChangeSelectedFund}
                        >
                            {fundList.map(f => (
                                <MenuItem key={f.id} value={f.id}>
                                    {f.name}
                                </MenuItem>
                            ))}
                        </CustomSelect>
                    </Box>
                    <TableContainer>
                        <Table sx={{ overflowX: 'auto' }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>{texts.SENDING_DATE}</TableCell>
                                    <TableCell>{texts.CLOSING_DATE}</TableCell>
                                    <TableCell>{texts.CREATOR}</TableCell>
                                    <TableCell>{texts.FUND}</TableCell>
                                    <TableCell>{texts.PREMIUM_LABEL}</TableCell>
                                    <TableCell>{texts.CLOSING_NBUMBER}</TableCell>
                                </TableRow>
                            </TableHead>
                            {!filteredClosings.length ? (
                                <EmptyTableBody colSpan={6} loading={dataLoader} />
                            ) : (
                                <TableBody>
                                    {filteredClosings.map(c => (
                                        <TableRow key={c.id}>
                                            <TableCell>
                                                {moment(c.activatedAt).format('L')}
                                            </TableCell>
                                            <TableCell>{moment(c.issueDate).format('L')}</TableCell>
                                            <TableCell>{parseFullName(c.activatedBy)}</TableCell>
                                            <TableCell>{c.investmentFund.name}</TableCell>
                                            <TableCell>{parsePremium(c.premium)}</TableCell>
                                            <TableCell>{c.comment}</TableCell>
                                        </TableRow>
                                    ))}
                                </TableBody>
                            )}
                        </Table>
                    </TableContainer>
                </Loader>
            </CustomPaper>
            <ConfirmDialog
                open={activateDialogOpen}
                title={texts.CLOSING_ACTIVATION}
                cancelText={texts.CANCEL}
                confirmText={texts.CONFIRMER}
                loading={activateLoading}
                handleClose={() => setActivateDialogOpen(false)}
                handleConfirm={handleActivate}
            >
                {texts.CONFIRM_CLOSING_ACTIVATION}
            </ConfirmDialog>
        </ListContainer>
    );
};

export default Closings;
